import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { PartialDeep } from 'type-fest';
import { withDataPoint } from '../../../diagnostics/client';
import { CSDisclaimerTest } from '../../../dto/events';
import {
  Provider,
  useGetTreatmentPrerequisitesQuery,
} from '../../../generated/graphql';
import { TALKIATRY_PHONE_NUMBER_DISPLAY } from '../../../helpers/const';
import { getMedicalDisclaimerStates } from '../../../helpers/states';
import { useAppDispatch } from '../../../pages/assessment';
import {
  selectPatient,
  selectReasonVisitAnswers,
} from '../../../redux/assessment';
import { selectLdFlags } from '../../../redux/ld-flags';
import {
  selectMatching,
  updateMatch,
  updateNoMatch,
} from '../../../redux/matching';
import { setLabel, setSublabel } from '../../../redux/meta';
import { canProviderPrescribeCSInState } from '../../../shared/licenses-to-eligible-states';
import { Icon } from '../../icon/icon';
import { useLDFlags } from '../../providers/ld-flags';
import { MedicalDisclaimer } from './medical-disclaimer';
import { ProviderSubsection } from './provider-subsection';
import ZocdocCancellationAlert from './zocdoc-cancellation-alert';
import ZocdocNotAMatchAlert from './zocdoc-not-a-match-alert';
import {
  trackSeeMoreMatchesClick,
  trackTopMatchesDisplayed,
} from '../../../helpers/analytics';

export const modalitiesMap = {
  remote: 'Remote-only',
  both: 'Both',
  'in-person': 'In-person',
};

const List = () => {
  const {
    shouldShowControlledSubstanceDisclaimer,
    medicalDisclaimerStates,
    shouldUseIntakeV2Point1,
  } = useSelector(selectLdFlags);

  const listOfMedicalDisclaimerStates = getMedicalDisclaimerStates(
    medicalDisclaimerStates
  );
  const dispatch = useAppDispatch();
  const reasonVisitAnswers = useSelector(selectReasonVisitAnswers);
  const { matches } = useSelector(selectMatching);
  const patient = useSelector(selectPatient);

  const topMatches = matches.slice(0, 5);
  const moreMatches = matches.slice(5, 8);
  const [seeMoreMatches, setSeeMoreMatches] = useState(false);

  const { bookingWidgetEnabled } = useLDFlags();

  const {
    data: treatmentPrerequisiteData,
    loading: treatmentPrerequisiteLoading,
  } = useGetTreatmentPrerequisitesQuery();

  const primaryTreatmentPrereqId =
    treatmentPrerequisiteData?.getTreatmentPrerequisites.find(
      t => t.name === 'ListedAsPrimary'
    ).id;

  const { addToast } = useToasts();

  const numberOfMatchesShown = topMatches.length + moreMatches.length;
  const title = shouldUseIntakeV2Point1
    ? 'Meet your matches'
    : numberOfMatchesShown === 1
    ? 'There is one psychiatrist who seems to be a great match for you!'
    : `There are ${numberOfMatchesShown} psychiatrists who seem to be a great match for you!`;

  const subtitle = shouldUseIntakeV2Point1
    ? numberOfMatchesShown === 1
      ? 'Based on the info you gave us, this clinician is the best fit for you.'
      : 'Based on the info you gave us, these clinicians are the best fit for you.'
    : '';

  const scheduleACallText = shouldUseIntakeV2Point1
    ? 'We’re here to help. Schedule a call with one of our care coordinators.'
    : 'We can still help you find the right match. Schedule a quick call with one of our care coordinators.';

  useEffect(() => {
    dispatch(setLabel(title));
    dispatch(setSublabel(subtitle));
  }, [title]);

  useEffect(() => {
    trackTopMatchesDisplayed(topMatches);
  }, []);

  const noMatchHeader = bookingWidgetEnabled
    ? shouldUseIntakeV2Point1
      ? "Don't see what you need?"
      : 'Don’t see the right match?'
    : numberOfMatchesShown === 1
    ? "This provider doesn't seem right to you?"
    : 'None of these providers seem right to you?';

  const noMatchButtonText = bookingWidgetEnabled
    ? shouldUseIntakeV2Point1
      ? 'Schedule a call'
      : 'Schedule Call'
    : `${
        numberOfMatchesShown === 1 ? 'This provider is' : 'These providers are'
      } NOT a match for me`;

  if (treatmentPrerequisiteLoading) return <CircularProgress />;

  const selectProvider = async (
    provider: PartialDeep<Provider>,
    appointmentTime?: string
  ) => {
    const providerInput = {
      id: provider.id,
      firstName: provider.firstName,
      lastName: provider.lastName,
      primaryCredential: provider.primaryCredential,
    };
    const updateMatchingInput = { provider: providerInput, appointmentTime };
    const errors = await dispatch(updateMatch(updateMatchingInput));
    if (errors) {
      addToast(
        'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
      return { status: 'error' };
    }
    return { status: 'success' };
  };

  const getRejectCSDisclaimerMetrics = (): CSDisclaimerTest => {
    const { RV_MainReason, RV_SecondaryReason, RV_SpecialtyCare } =
      reasonVisitAnswers;

    const numberOfMatchesWithCSLicense = matches.filter(match => {
      const canPrescribeCSInState = canProviderPrescribeCSInState(
        match.licenses,
        patient.addressState
      );

      return canPrescribeCSInState;
    }).length;

    return {
      should_show_controlled_substance_disclaimer:
        shouldShowControlledSubstanceDisclaimer,
      rv_main_reason: RV_MainReason,
      rv_secondary_reason: RV_SecondaryReason,
      rv_specialty_care: RV_SpecialtyCare,
      number_of_matches: matches.length,
      number_of_matches_with_cs_license: numberOfMatchesWithCSLicense,
      chose_no_match: true,
      chose_cs_provider: false,
    };
  };

  const rejectAll = async () => {
    await withDataPoint('reject_matches', async dp => {
      const errors = await dispatch(updateNoMatch());
      if (errors) dp.error = errors;

      const CSDisclaimerMetrics = getRejectCSDisclaimerMetrics();
      Object.entries(CSDisclaimerMetrics).forEach(([k, v]) => {
        dp[k] = v;
      });
      if (errors) {
        addToast(
          'Sorry, something went wrong. Please refresh the page and try again.',
          { appearance: 'error' }
        );
        return { status: 'error' };
      }
      return { status: 'success' };
    });
  };

  const shouldShowMedicalDisclaimer = listOfMedicalDisclaimerStates.includes(
    patient.addressState
  );

  const handleSeeMoreMatches = () => {
    trackSeeMoreMatchesClick(moreMatches);
    setSeeMoreMatches(true);
  };

  const renderProviderCard = (
    provider: Provider,
    wasProviderAdditionalMatch: boolean = false
  ) => {
    const onSelectAppointmentTime = async (appointmentTime?: string) =>
      selectProvider(provider, appointmentTime);

    return (
      <ProviderSubsection
        selectProvider={onSelectAppointmentTime}
        {...provider}
        specialties={provider.specialties.filter(
          specialty =>
            specialty.treatmentPrerequisiteId === primaryTreatmentPrereqId
        )}
        patientAddressState={patient.addressState}
        key={provider.id}
        shouldShowCSDisclaimer={
          shouldShowControlledSubstanceDisclaimer &&
          !shouldShowMedicalDisclaimer &&
          !canProviderPrescribeCSInState(
            provider.licenses,
            patient.addressState
          )
        }
        wasProviderAdditionalMatch={wasProviderAdditionalMatch}
      />
    );
  };

  return (
    <>
      {!bookingWidgetEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          If you were referred to a provider who is not on this list, you’re
          welcome to call us at {TALKIATRY_PHONE_NUMBER_DISPLAY}. Otherwise, we
          strongly encourage you to book with one of the matches here.
        </Alert>
      )}
      {shouldShowMedicalDisclaimer && <MedicalDisclaimer />}
      <ZocdocNotAMatchAlert />
      <ZocdocCancellationAlert />
      {topMatches.map(provider => renderProviderCard(provider))}
      {moreMatches.length > 0 && (
        <Collapse in={seeMoreMatches} appear={seeMoreMatches} timeout={300}>
          <>{moreMatches.map(provider => renderProviderCard(provider, true))}</>
        </Collapse>
      )}
      {!seeMoreMatches && moreMatches.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6">Want to see more matches?</Typography>
          <Typography>
            View other doctors who can provide excellent care.
          </Typography>
          <IconButton
            size="large"
            onClick={handleSeeMoreMatches}
            sx={{ mt: 3, width: '100%', borderRadius: 3 }}
            color="primary"
          >
            <Icon icon="ArrowsRotate" color="inherit" />
            <Typography sx={{ ml: 2 }} fontWeight={500} fontSize="15px">
              See more matches
            </Typography>
          </IconButton>
        </Box>
      )}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">{noMatchHeader}</Typography>
        {bookingWidgetEnabled && <Typography>{scheduleACallText}</Typography>}
        <Button
          fullWidth
          size="large"
          onClick={rejectAll}
          variant="outlined"
          sx={{ mt: 3, mb: 3 }}
        >
          {noMatchButtonText}
        </Button>
      </Box>
    </>
  );
};

export default List;
